'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = ({ className = '' }: { className?: string }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: 'What happens after I submit the consultation form?',
      answer: 'Within 24 hours, a member of our strategic team will review your information and reach out to schedule your free 30-minute strategy session. During this call, we\'ll discuss your specific challenges, goals, and how our AI solutions can deliver measurable results for your business.'
    },
    {
      question: 'How long does the consultation process take?',
      answer: 'The initial consultation is a 30-minute strategy session where we assess your needs and provide preliminary recommendations. If we\'re a good fit, we\'ll schedule a more detailed discovery session (60-90 minutes) to create a customized implementation roadmap with specific timelines and ROI projections.'
    },
    {
      question: 'Is there any cost for the initial consultation?',
      answer: 'No, the initial 30-minute strategy session is completely free with no obligation. We believe in demonstrating value upfront and only proceed with paid engagements when we\'re confident we can deliver measurable results for your organization.'
    },
    {
      question: 'What information should I prepare for the consultation?',
      answer: 'Come prepared to discuss your current business challenges, growth goals, existing technology stack, and any specific pain points you\'re experiencing. Having rough numbers on your current metrics (conversion rates, customer acquisition costs, support ticket volumes) helps us provide more targeted recommendations.'
    },
    {
      question: 'Do you work with companies of all sizes?',
      answer: 'We primarily work with growth-stage companies (50-500 employees) and established businesses looking to scale. Our solutions are designed for organizations ready to invest in strategic AI implementation with clear ROI expectations. We also work with private equity portfolio companies requiring rapid operational optimization.'
    },
    {
      question: 'What makes Red Letter X different from other AI consultancies?',
      answer: 'We focus exclusively on measurable business outcomes rather than just implementing technology. Every engagement includes clear ROI targets, detailed success metrics, and ongoing optimization. We act as strategic partners, not just vendors, ensuring long-term success through continuous improvement and adaptation.'
    },
    {
      question: 'How quickly can we see results?',
      answer: 'Timeline varies based on project scope, but most clients see initial results within 30-60 days of implementation. Quick wins like automated lead qualification or customer support chatbots can show impact within weeks, while comprehensive revenue growth systems typically demonstrate full ROI within 3-6 months.'
    },
    {
      question: 'What if we\'re not ready to commit after the consultation?',
      answer: 'Absolutely no pressure. The consultation is designed to provide value regardless of whether you move forward. You\'ll receive actionable insights and recommendations that you can implement independently. Many clients start with smaller pilot projects before committing to comprehensive engagements.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`bg-card rounded-xl shadow-soft p-8 ${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto">
          Get answers to common questions about our consultation process
        </p>
      </div>

      <div className="space-y-4 max-w-3xl mx-auto">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-soft"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors duration-300"
            >
              <span className="text-base font-heading font-semibold text-foreground pr-4">
                {faq.question}
              </span>
              <Icon
                name="ChevronDownIcon"
                size={24}
                className={`text-accent flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96' : 'max-h-0'
              }`}
            >
              <div className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Still have questions? We're here to help.
        </p>
        <a
          href="mailto:hello@redletterx.ai"
          className="inline-flex items-center space-x-2 text-accent hover:text-accent/80 font-semibold transition-colors duration-300"
        >
          <Icon name="EnvelopeIcon" size={20} />
          <span>Email us directly</span>
        </a>
      </div>
    </div>
  );
};

export default FAQSection;